/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/config.ts
 * PURPOSE: Environment-based configuration for assistant streaming
 * 
 * DEPENDENCIES: import.meta.env (Vite)
 * 
 * PUBLIC API:
 *   - ASSISTANT_CONFIG: Configuration object with all settings
 * 
 * SIDE EFFECTS: None (pure configuration)
 * 
 * AGENT SAFETY: 🟢 SAFE - Configuration only
 * FILE SIZE: 50 / 300 lines
 * 
 * Agent Notes:
 * - Environment variables for deployment configuration
 * - Sensible defaults for development
 * - Last updated: 2026-01-27
 * 
 * See: /README.md | /ARCHITECTURE.md
 */

import { DEFAULT_RETRY_CONFIG, type RetryConfig } from './types';

type RuntimeAppConfig = {
  apiBaseUrl?: string;
  assistantUnifiedOrchestrationStream?: boolean | string;
  /** Canonical deployment environment injected via /config.js on hosted builds [WS0]. */
  env?: string;
};

/** Read Azure/runtime toggle from /config.js (injected by entrypoint.sh before bundle load). */
export function resolveAssistantUnifiedOrchestrationStream(): boolean {
  if (typeof window !== 'undefined') {
    const w = window as Window & { __APP_CONFIG__?: RuntimeAppConfig };
    const runtime = w.__APP_CONFIG__?.assistantUnifiedOrchestrationStream;
    if (runtime === true || runtime === 'true') return true;
    if (runtime === false || runtime === 'false') return false;
  }
  return import.meta.env.VITE_ASSISTANT_UNIFIED_ORCHESTRATION_STREAM === 'true';
}

export type AppEnv = 'local' | 'azure-dev' | 'production';

/**
 * Canonical deployment environment for the browser — the single source of truth so the shell can
 * act per environment [WS0]. Prefers the runtime `env` injected into /config.js (entrypoint.sh),
 * then a build-time `VITE_APP_ENV`, then derives from the unified-stream flag (on for hosted
 * builds, off locally) so it is already correct before an explicit value is wired on Azure.
 */
export function resolveAppEnv(): AppEnv {
  const norm = (v: unknown): AppEnv | '' => {
    const s = String(v ?? '').trim().toLowerCase();
    if (s === 'local' || s === 'development' || s === 'dev') return 'local';
    if (s === 'azure-dev' || s === 'azure_dev' || s === 'azure') return 'azure-dev';
    if (s === 'production' || s === 'prod') return 'production';
    return '';
  };
  if (typeof window !== 'undefined') {
    const w = window as Window & { __APP_CONFIG__?: RuntimeAppConfig };
    const fromRuntime = norm(w.__APP_CONFIG__?.env);
    if (fromRuntime) return fromRuntime;
  }
  const fromBuild = norm(import.meta.env.VITE_APP_ENV);
  if (fromBuild) return fromBuild;
  return resolveAssistantUnifiedOrchestrationStream() ? 'azure-dev' : 'local';
}

export const isLocalEnv = (): boolean => resolveAppEnv() === 'local';
export const isHostedEnv = (): boolean => resolveAppEnv() !== 'local';

/**
 * Assistant streaming configuration
 * 
 * Environment variables:
 * - VITE_ASSISTANT_STREAM_URL: Override stream endpoint (default: /api/v1/assistant/chat/stream)
 * - VITE_ASSISTANT_MAX_RETRIES: Max retry attempts (default: 3)
 * - VITE_ASSISTANT_RETRY_INITIAL_DELAY_MS: Initial retry delay in ms (default: 1000)
 * - VITE_ASSISTANT_RETRY_MAX_DELAY_MS: Max retry delay in ms (default: 10000)
 * - VITE_ASSISTANT_UNIFIED_ORCHESTRATION_STREAM: Dock uses orchestration GET stream (Langflow topic routing)
 * - VITE_DEFAULT_AGENT_ID: Platform agent UUID for unified stream (optional; see resolveDefaultAgentId)
 */
export const ASSISTANT_UNIFIED_ORCHESTRATION_STREAM =
  resolveAssistantUnifiedOrchestrationStream();

export const ASSISTANT_CONFIG = {
  /**
   * Stream endpoint path (relative to API_BASE_URL)
   * Uses GET endpoint for EventSource compatibility (matching sales intelligence pattern)
   */
  streamUrl: import.meta.env.VITE_ASSISTANT_STREAM_URL || '/api/v1/assistant/chat/stream',

  /**
   * Retry configuration
   */
  retry: {
    maxRetries: Number.parseInt(
      import.meta.env.VITE_ASSISTANT_MAX_RETRIES || String(DEFAULT_RETRY_CONFIG.maxRetries),
      10
    ),
    initialDelay: Number.parseInt(
      import.meta.env.VITE_ASSISTANT_RETRY_INITIAL_DELAY_MS || String(DEFAULT_RETRY_CONFIG.initialDelay),
      10
    ),
    maxDelay: Number.parseInt(
      import.meta.env.VITE_ASSISTANT_RETRY_MAX_DELAY_MS || String(DEFAULT_RETRY_CONFIG.maxDelay),
      10
    ),
    backoffMultiplier: DEFAULT_RETRY_CONFIG.backoffMultiplier,
  } as RetryConfig,
} as const;
